import React, { useEffect, useRef, useState } from 'react';

export interface RowAction {
  label: string;
  onClick: () => void;
  tone?: 'default' | 'primary' | 'success' | 'danger' | 'warning';
  hidden?: boolean;
  icon?: React.ReactNode;
}

interface RowActionsProps {
  actions: RowAction[];
}

const toneClasses: Record<NonNullable<RowAction['tone']>, string> = {
  default: 'text-gray-700 hover:text-gray-900',
  primary: 'text-indigo-600 hover:text-indigo-900',
  success: 'text-green-600 hover:text-green-900',
  danger: 'text-red-600 hover:text-red-900',
  warning: 'text-blue-600 hover:text-blue-900',
};

const toneMenuClasses: Record<NonNullable<RowAction['tone']>, string> = {
  default: 'text-gray-700 hover:bg-gray-50',
  primary: 'text-indigo-600 hover:bg-indigo-50',
  success: 'text-green-600 hover:bg-green-50',
  danger: 'text-red-600 hover:bg-red-50',
  warning: 'text-blue-600 hover:bg-blue-50',
};

const RowActions: React.FC<RowActionsProps> = ({ actions }) => {
  const visible = actions.filter((a) => !a.hidden);
  const [open, setOpen] = useState(false);
  const [dropUp, setDropUp] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  const handleToggle = () => {
    if (!open && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const estimated = visible.length * 44 + 8;
      setDropUp(spaceBelow < estimated && rect.top > estimated);
    }
    setOpen((v) => !v);
  };

  return (
    <>
      {/* Desktop: inline buttons */}
      <div className="hidden sm:flex items-center gap-3">
        {visible.map((action) => (
          <button
            key={action.label}
            onClick={action.onClick}
            className={`text-sm font-medium ${toneClasses[action.tone || 'default']}`}
          >
            {action.label}
          </button>
        ))}
      </div>

      {/* Mobile: kebab menu */}
      <div ref={wrapperRef} className="relative sm:hidden">
        <button
          ref={buttonRef}
          type="button"
          onClick={handleToggle}
          aria-haspopup="menu"
          aria-expanded={open}
          className="w-9 h-9 flex items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100"
        >
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path d="M10 6a2 2 0 110-4 2 2 0 010 4zm0 6a2 2 0 110-4 2 2 0 010 4zm0 6a2 2 0 110-4 2 2 0 010 4z" />
          </svg>
        </button>
        {open && (
          <div
            role="menu"
            className={`absolute right-0 z-50 w-48 bg-white border border-gray-200 rounded-xl shadow-lg py-1 ${
              dropUp ? 'bottom-full mb-2' : 'top-full mt-2'
            }`}
          >
            {visible.map((action) => (
              <button
                key={action.label}
                role="menuitem"
                onClick={() => {
                  setOpen(false);
                  action.onClick();
                }}
                className={`w-full text-left px-4 py-3 text-sm font-medium ${toneMenuClasses[action.tone || 'default']}`}
              >
                {action.label}
              </button>
            ))}
          </div>
        )}
      </div>
    </>
  );
};

export default RowActions;
